import { Result, Spin, theme } from "antd";
import WishlistItemView from "./Wishlist/WishlistItemView";
import DocumentTitle from "react-document-title";
import { generatePath, Link, useNavigate, useParams } from "react-router";
import { PropsWithChildren, useEffect } from "react";
import { RoutePaths } from "../routes";
import "./wishlist.css";
import MobileHeader from "../Components/MobileHeader";
import PlusWishButton from "../Components/MobileHeader/PlusWishButton";
import MobileBottomSpacer from "../Components/MobileBottomSpacer";
import {
  useCurrentUser,
  useProfile,
  useIsMyWishlist,
  useLoadMyDefaultWishlistId,
  useWishlist,
} from "../../data/hooks";
import { apostrophizeName } from "../../data/strings";
import OwnerCard from "./Wishlist/OwnerCard";
import { Id, Wishlist as WishlistType } from "../../data/types";
import { SmileOutlined } from "@ant-design/icons";
import Button from "../Components/Button";
import { Profile } from "../../data/firestore/profile";
import { analyticsLogEvent } from "../../data/firebase/analytics";
import { Empty } from "./Wishlist/Empty";
import { useSelector } from "react-redux";
import { selectIsLoadingWishlist } from "@/data/redux/wishlist/selectors";

interface WishlistWrapperProps {
  wishlistId?: Id;
  isMyWishlist?: boolean;
  wishlistOwnerProfile?: Profile;
  wishlist?: WishlistType;
}

function WishlistWrapper({
  children,
  wishlistId,
  isMyWishlist,
  wishlistOwnerProfile,
  wishlist,
}: PropsWithChildren<WishlistWrapperProps>) {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const title = isMyWishlist
    ? "My wishlist"
    : apostrophizeName(wishlistOwnerProfile?.displayName) + " wishlist";
  const headers = (
    <>
      <MobileHeader title={title}>
        {isMyWishlist ? <PlusWishButton wishlistId={wishlistId} /> : <></>}
      </MobileHeader>
      {!isMyWishlist && wishlist && (
        <>
          <OwnerCard />
        </>
      )}
    </>
  );

  return (
    <DocumentTitle title={title}>
      <>
        {headers}
        <div
          style={{
            padding: 24,
            minHeight: 360,
            background: colorBgContainer,
          }}
          data-testid="wishlist"
        >
          {children}
        </div>
      </>
    </DocumentTitle>
  );
}

function InnerWishlist() {
  const { wishlistId } = useParams();
  const { wishlist, isLoaded, reserved } = useWishlist();
  const isMyWishlist = useIsMyWishlist();
  const { user } = useCurrentUser();
  const isLoading = useSelector(selectIsLoadingWishlist);

  if (!isLoaded || isLoading) {
    return <Spin />;
  }

  if (!wishlist) {
    const path = wishlistId
      ? generatePath(RoutePaths.specificWishlist, { wishlistId })
      : generatePath(RoutePaths.root);

    analyticsLogEvent("wishlist_not_found", { wishlistId });

    return (
      <Result
        icon={<SmileOutlined />}
        title="Wishlist not found"
        subTitle="Maybe you got a wrong link?"
        extra={
          <Link to={path} viewTransition>
            <Button>Back home</Button>
          </Link>
        }
      />
    );
  }

  if (!wishlist.items || wishlist.items.length === 0) {
    return <Empty isMyWishlist={isMyWishlist} />;
  }

  return (
    <>
      <div className="flex flex-wrap justify-center gap-8">
        {wishlist.items.map((item) => {
          if (!item.id) {
            return;
          }

          const reservedRec = reserved?.find(
            (r) => r.wishlistId === wishlistId && r.wishId === item.id,
          );

          return (
            <WishlistItemView
              item={item}
              wishlistId={wishlist.id}
              key={item.createdAt}
              userId={user?.uid}
              isMyWishlist={isMyWishlist}
              reservedRec={reservedRec}
            />
          );
        })}
      </div>
      <MobileBottomSpacer />
    </>
  );
}

export default function Wishlist() {
  const { wishlistId } = useParams();
  const { wishlist } = useWishlist();
  const wishlistOwnerProfile = useProfile(wishlist?.userId);
  const isMyWishlist = useIsMyWishlist();
  const defaultWishlistId = useLoadMyDefaultWishlistId();
  const navigate = useNavigate();
  const { user, authStateLoaded } = useCurrentUser();

  useEffect(() => {
    if (!wishlistId) {
      if (defaultWishlistId) {
        analyticsLogEvent("redirecting_to_default");
        navigate(
          generatePath(RoutePaths.specificWishlist, {
            wishlistId: defaultWishlistId,
          }),
        );
      } else if (authStateLoaded && !user) {
        analyticsLogEvent("redirecting_logged_out");
        navigate(generatePath(RoutePaths.root));
      }
    }
  }, [wishlistId, defaultWishlistId, wishlist?.id, authStateLoaded]);

  return (
    <WishlistWrapper
      wishlistId={wishlistId}
      wishlistOwnerProfile={wishlistOwnerProfile}
      isMyWishlist={isMyWishlist}
      wishlist={wishlist}
    >
      <InnerWishlist />
      <MobileBottomSpacer />
    </WishlistWrapper>
  );
}
